// Shared MyTools API calls — extracted from MyToolsPage.tsx so other features
// (e.g. claudeagents' "Publish to Marketplace" and the skill-share resolve flow)
// can drive the same upsert/publish/install endpoints without importing a
// feature-private module.

import { isWidgetsMockEnabled, getMockWidgetBundle, buildAndStoreMockBundle, getMockInstallConfig, setMockInstallConfig, replaceMockInstallConfig, getMockWidgetBundleHistory, getMockWidgetAsset } from './widgetsMock';
import { applyInstallControls, pendingPermissions, readInstallControls, stripControlKeys, type WidgetInstallControls } from '../features/widgets/widgetInstallControls';
import { notifyWidgetInstallsChanged } from '../features/widgets/widgetEvents';
import type { StoreDraft, StoreListing, StoreReview } from '../features/mytools/storeMock';

const API_BASE = process.env.REACT_APP_MyTools_Url || process.env.REACT_APP_Auth_Url || '';
const MYAMP_BASE = process.env.REACT_APP_MyAMP_Url || '';

function authHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
  };
}

async function apiError(res: Response, fallback: string): Promise<Error> {
  const body = await res.json().catch(() => ({}));
  return new Error(typeof body?.message === 'string' ? body.message : `${fallback} (${res.status})`);
}

export async function getMarketplaceStore(toolId: string): Promise<StoreListing> {
  const res = await fetch(`${API_BASE}/api/MyTools/${toolId}/store`, { headers: authHeaders() });
  if (!res.ok) throw await apiError(res, 'Failed to load the store page');
  const json = await res.json();
  const data = json?.data ?? {};
  const releases = Array.isArray(data.releases) ? data.releases : [];
  let mcpTools: { name: string; description: string }[] = [];
  const rawMcpTools = releases.find((r: any) => r.mcpTools)?.mcpTools;
  if (typeof rawMcpTools === 'string') {
    try { mcpTools = JSON.parse(rawMcpTools); } catch { /* invalid historical data */ }
  }
  return {
    iconUrl: data.iconUrl ?? undefined,
    video: data.videoUrl ? { kind: 'embed', url: data.videoUrl } : null,
    screenshots: (data.screenshots ?? []).map((s: any) => s.url),
    storeDescription: data.storeDescription ?? undefined,
    pendingVersion: data.pendingVersion ?? undefined,
    pendingReleaseNotes: data.pendingReleaseNotes ?? undefined,
    reviews: (data.reviews ?? []).map((r: any) => ({
      id: String(r.id), reviewer: r.reviewer ?? 'Agency user', agency: r.agency ?? 'Agency',
      rating: Number(r.rating), comment: r.comment ?? '', date: r.updatedAt,
    })),
    releases: releases.map((r: any) => ({ version: r.version ?? '', notes: r.notes ?? '', date: r.date })),
    mcpTools,
    sample: false,
  };
}

export async function saveMarketplaceStore(toolId: string, draft: StoreDraft): Promise<StoreListing> {
  const res = await fetch(`${API_BASE}/api/MyTools/${toolId}/store`, {
    method: 'PUT', headers: authHeaders(), body: JSON.stringify({
      iconUrl: draft.iconUrl || null,
      videoUrl: draft.videoUrl || null,
      storeDescription: draft.storeDescription || null,
      screenshots: draft.screenshots,
      version: draft.version || null,
      releaseNotes: draft.releaseNotes || null,
    }),
  });
  if (!res.ok) throw await apiError(res, 'Failed to save the store page');
  return getMarketplaceStore(toolId);
}

export async function saveMarketplaceReview(toolId: string, rating: number, comment: string): Promise<StoreReview> {
  const res = await fetch(`${API_BASE}/api/MyTools/${toolId}/store/review`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify({ rating, comment }),
  });
  if (!res.ok) throw await apiError(res, 'Failed to save the review');
  const json = await res.json();
  const r = json.data;
  return { id: String(r.id), reviewer: r.reviewer, agency: r.agency, rating: r.rating, comment: r.comment, date: r.updatedAt };
}

export async function releaseMarketplaceListing(toolId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/MyTools/${toolId}/release`, {
    method: 'POST', headers: authHeaders(),
  });
  if (!res.ok) throw await apiError(res, 'Failed to release the listing');
}

export async function draftMarketplaceDescription(input: { name: string; type: string; facts: string[] }): Promise<string> {
  const res = await fetch(`${MYAMP_BASE}/api/v1/claude-managed/marketplace/listing-description`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify(input),
  });
  if (!res.ok) throw await apiError(res, 'Failed to draft the listing description');
  const json = await res.json();
  const description = json?.data?.description;
  if (typeof description !== 'string' || !description.trim()) throw new Error('The AI returned an empty description.');
  return description.trim();
}

export interface UpsertMyToolConfigFieldSchema {
  key: string;
  label: string;
  type: 'text' | 'toggle' | 'select';
  options?: string[];
  requiredFromInstaller: boolean;
  hint?: string;
  scope?: 'shared' | 'per_user';
}

export interface UpsertMyToolPayload {
  id?: number;
  type: number;
  name: string;
  category: string;
  icon: string;
  description: string;
  skills: string;
  kbFileNames: string[];
  visibility: number;
  mcpUrl?: string;
  mcpHeadersTemplate?: Record<string, string>[];
  configSchema?: UpsertMyToolConfigFieldSchema[];
  skillContent?: string;
  agentShareToken?: string;
  // Skill type only, optional — the originating Digital Workforce skill's own
  // stable id, so a later share attempt for the same live skill can find and
  // reuse this row instead of minting a duplicate. See SkillShareMenuModal.
  sourceSkillId?: string;
  // Marketplace groups (see features/mytools/marketplaceGroups.tsx). Omit to keep the stored groups
  // on edit / use the type's defaults on create. Widget placement travels with the bundle upload.
  groups?: string[];
}

/** The upsert endpoint rejects invalid saves with a specific reason in the body
 * (`{ success: false, message: "Skill content is required" }` and similar for a
 * missing MCP URL or an installer-supplied header carrying a stored value).
 * Reporting only the status code threw all of that away and left the user with
 * "Failed to save tool (400)" and nothing to act on. */
async function saveToolError(res: Response): Promise<Error> {
  let message = '';
  try {
    const body = await res.json();
    message = typeof body?.message === 'string' ? body.message : '';
  } catch {
    // Not JSON, or an empty body - fall back to the status below.
  }
  return new Error(message || `Failed to save tool (${res.status})`);
}

export async function upsertMyTool(payload: UpsertMyToolPayload): Promise<string> {
  const res = await fetch(`${API_BASE}/api/MyTools/upsert`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await saveToolError(res);
  const json = await res.json();
  return String(json.id ?? payload.id ?? '');
}

/** Same as upsertMyTool, but also returns the Python-side skill_id the save produced
 * (needed to attach a freshly-minted skill copy to an agent during Share Agent import). */
export async function upsertMyToolWithSkillId(payload: UpsertMyToolPayload): Promise<{ id: string; skillId: string }> {
  const res = await fetch(`${API_BASE}/api/MyTools/upsert`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await saveToolError(res);
  const json = await res.json();
  return { id: String(json.id ?? payload.id ?? ''), skillId: String(json.skillId ?? '') };
}

/** Looks up whether the current user already shared this live Digital Workforce
 * skill before — returns the existing tool's id, or null if this would be the
 * first share. */
export async function findToolBySourceSkillId(sourceSkillId: string): Promise<string | null> {
  const res = await fetch(`${API_BASE}/api/MyTools/by-source-skill/${encodeURIComponent(sourceSkillId)}`, {
    headers: authHeaders(),
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to look up existing share (${res.status})`);
  const json = await res.json();
  return json?.id != null ? String(json.id) : null;
}

export interface MyToolSummary {
  id: string;
  name: string;
  type: 'skill' | 'agent' | 'workflow' | 'widget' | 'mcp_server' | 'integration';
  visibility: 'just_me' | 'my_agency' | 'published';
  publishStatus?: 'pending' | 'approved' | 'rejected' | 'changes_requested';
  isAgencyInstalled?: boolean;
  // Full enough to safely round-trip through upsertMyTool (e.g. to change just
  // visibility) without a partial upsert clobbering the rest of the row — the
  // backend UPDATE overwrites every one of these columns unconditionally, so a
  // caller must always send back every field it got, not just the one it changed.
  category: string;
  icon: string;
  description: string;
  skills: string;
  kbFileNames: string[];
  mcpUrl?: string;
  mcpHeadersTemplate?: Record<string, string>[];
  configSchema?: UpsertMyToolConfigFieldSchema[];
  skillContent?: string;
  agentShareToken?: string;
}

const MYTOOL_INT_TO_TYPE: Record<number, MyToolSummary['type']> = {
  0: 'skill', 1: 'agent', 2: 'workflow', 3: 'widget', 4: 'mcp_server', 5: 'integration',
};
const MYTOOL_INT_TO_VISIBILITY: Record<number, MyToolSummary['visibility']> = {
  0: 'just_me', 1: 'my_agency', 2: 'published',
};

/** Fetches a tool the caller already knows the id of (e.g. a Digital Workforce
 * MCP connection's linked My Creations listing) — enough to both drive
 * ToolShareMenuModal and safely round-trip through upsertMyTool. */
export async function getMyTool(toolId: string): Promise<MyToolSummary | null> {
  const res = await fetch(`${API_BASE}/api/MyTools/${toolId}`, { headers: authHeaders() });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to load tool (${res.status})`);
  const json = await res.json();
  const data = json?.data ?? {};
  let kbFileNames: string[] = [];
  if (typeof data.kbFileNames === 'string') {
    try { kbFileNames = JSON.parse(data.kbFileNames); } catch { /* ignore */ }
  } else if (Array.isArray(data.kbFileNames)) {
    kbFileNames = data.kbFileNames;
  }
  return {
    id: String(data.id),
    name: data.name ?? '',
    type: MYTOOL_INT_TO_TYPE[data.type] ?? 'skill',
    visibility: MYTOOL_INT_TO_VISIBILITY[data.visibility] ?? 'just_me',
    publishStatus: data.publishStatus ?? undefined,
    isAgencyInstalled: !!data.isAgencyInstalled,
    category: data.category ?? '',
    icon: data.icon ?? '',
    description: data.description ?? '',
    skills: data.skills ?? '',
    kbFileNames,
    mcpUrl: data.mcpUrl ?? undefined,
    mcpHeadersTemplate: data.mcpHeadersTemplateList ?? undefined,
    configSchema: data.configSchemaList ?? undefined,
    skillContent: data.skillContent ?? undefined,
    agentShareToken: data.agentShareToken ?? undefined,
  };
}

export interface AgentToolRef {
  id: string;
  isAgencyInstalled: boolean;
}

/** The caller's existing my_tools row for an agent, matched through any of the
 * agent's active share tokens (a my_tools row only stores its share token, not
 * the agent id). Prefers one already installed for the agency. Lets Digital
 * Workforce's Install for Agency reuse the same row instead of minting a new
 * one per click - a new row means a new tool id, which defeats the duplicate check. */
export async function findAgentTool(shareTokens: string[]): Promise<AgentToolRef | null> {
  if (shareTokens.length === 0) return null;
  const res = await fetch(`${API_BASE}/api/MyTools/my`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`Failed to load your tools (${res.status})`);
  const json = await res.json();
  const tokens = new Set(shareTokens);
  const matches = ((json?.data ?? []) as { id: number; type: number; agentShareToken?: string; isAgencyInstalled?: boolean }[])
    .filter(t => t.type === 1 && !!t.agentShareToken && tokens.has(t.agentShareToken));
  const match = matches.find(t => t.isAgencyInstalled) ?? matches[0];
  return match ? { id: String(match.id), isAgencyInstalled: !!match.isAgencyInstalled } : null;
}

export async function submitPublishRequest(toolId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/MyTools/${toolId}/publish-request`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (res.status === 409) throw new Error('already_pending');
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { message?: string }).message || 'Failed to submit for review');
  }
}

/** What a personal install routed to, and the ids it created. */
export interface InstallToolResult {
  routes: string[];
  skillId?: string;
  externalMcpTransactionId?: string;
}

export async function installTool(toolId: string, mcpFieldValues?: Record<string, string>, source?: 'marketplace' | 'share_link', connectionValues?: Record<string, string>): Promise<InstallToolResult> {
  const res = await fetch(`${API_BASE}/api/MyTools/install`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ toolId: Number(toolId), mcpFieldValues, source, connectionValues }),
  });
  const body = await res.json().catch(() => ({}));
  if (res.status === 409) throw new Error('already_installed');
  if (!res.ok || body?.success === false) {
    throw new Error((body as { message?: string }).message || `Failed to install tool (${res.status})`);
  }
  if (mcpFieldValues && getMockWidgetBundle(toolId)) setMockInstallConfig(toolId, mcpFieldValues);
  notifyWidgetInstallsChanged();
  return {
    routes: Array.isArray(body?.routes) ? body.routes : [],
    skillId: body?.skillId || undefined,
    externalMcpTransactionId: body?.externalMcpTransactionId || undefined,
  };
}

// Agency Admin only — installs a tool for every active user in the company.
export async function installToolForAgency(toolId: string, mcpFieldValues?: Record<string, string>, connectionValues?: Record<string, string>): Promise<void> {
  const res = await fetch(`${API_BASE}/api/MyTools/${toolId}/install-for-agency`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ mcpFieldValues, connectionValues }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.success === false) {
    throw new Error((body as { message?: string }).message || `Failed to install for agency (${res.status})`);
  }
  notifyWidgetInstallsChanged();
}

// Agency Admin only - uninstalls a tool for everyone it was installed for through the agency.
export async function uninstallToolForAgency(toolId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/MyTools/${toolId}/uninstall-for-agency`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok)
    throw new Error(`Failed to uninstall for agency (${res.status})`);
  notifyWidgetInstallsChanged();
}

export interface AgentAgencyInstallResult {
  user_id: string;
  // pending_setup: in the agency but not set up yet (setup runs on its own at their
  // next sign-in) - they get the agent automatically once it has. owner: the
  // sharer, who keeps the original.
  status?: 'installed' | 'owner' | 'pending_setup' | 'error';
  agent_id?: string;
  attached_mcps?: { name: string; url: string }[];
  needs_setup_mcps?: { name: string; url: string }[];
  // Bundled API integrations whose agency key the admin still has to add.
  integrations_needing_key?: string[];
  error?: string;
}

export interface AgentAgencyInstallResponse {
  results: AgentAgencyInstallResult[];
  alreadyInstalled: number;
}

export interface AgentAgencyInstallSummary {
  created: number;
  pendingSetup: number;
  failed: number;
  needsMcpSetup: number;
  alreadyInstalled: number;
}

export function summarizeAgentAgencyInstall({ results, alreadyInstalled }: AgentAgencyInstallResponse): AgentAgencyInstallSummary {
  const created = results.filter(r => r.status === 'installed');
  return {
    created: created.length,
    pendingSetup: results.filter(r => r.status === 'pending_setup').length,
    failed: results.filter(r => r.status === 'error').length,
    needsMcpSetup: created.filter(r => (r.needs_setup_mcps?.length ?? 0) > 0).length,
    alreadyInstalled,
  };
}

// Agency Admin only — creates a brand-new agent (independent copy, never a shared
// agent_id) for every active user in the company from a shared agent's snapshot.
export async function installAgentForAgency(toolId: string): Promise<AgentAgencyInstallResponse> {
  const res = await fetch(`${API_BASE}/api/MyTools/${toolId}/install-agent-for-agency`, {
    method: 'POST',
    headers: authHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.success === false) {
    throw new Error((body as { message?: string }).message || "We couldn't install this agent for your agency. Please try again.");
  }
  return {
    results: (body.results ?? []) as AgentAgencyInstallResult[],
    alreadyInstalled: Number(body.alreadyInstalled ?? 0),
  };
}

// GET /api/MyTools/installed also backfills anything installed for the user's agency
// that they don't have yet (agents included). Call it right after Digital Workforce
// setup so agency-installed agents show up immediately. Best-effort - never throws.
export async function syncAgencyInstalls(): Promise<void> {
  try {
    await fetch(`${API_BASE}/api/MyTools/installed`, { headers: authHeaders() });
  } catch {
    // The My Tools page runs the same sync on its next load.
  }
}

export type ShareScope = 'agency' | 'anyone';

export interface Share {
  id: string;
  token: string;
  scope: ShareScope;
  isActive: boolean;
  createdAt: string;
  revokedAt?: string;
}

export interface ResolvedShare {
  copiedToolId: string;
  toolType: 'skill' | 'agent' | 'workflow' | 'widget' | 'mcp_server' | 'integration';
  toolName: string;
  toolDescription: string;
  toolIcon: string;
  toolCategory: string;
  mcpUrl?: string;
  mcpHeaderKeys: string[];
  // Values the installer must supply for an MCP server (never any values themselves).
  mcpInstallerFields: ShareInstallerField[];
  // Integrations: the copy's definition in MyAMP (preview, API key, agents).
  apiIntegrationId?: number;
  isAgencyOwner: boolean;
}

export interface ShareInstallerField {
  key: string;
  label: string;
  hint?: string;
  scope: 'shared' | 'per_user';
}

const RESOLVE_INT_TO_TOOL_TYPE: Record<number, ResolvedShare['toolType']> = {
  0: 'skill', 1: 'agent', 2: 'workflow', 3: 'widget', 4: 'mcp_server', 5: 'integration',
};

export async function createShare(toolId: string, scope: ShareScope): Promise<{ id: string; token: string; scope: ShareScope }> {
  const res = await fetch(`${API_BASE}/api/MyTools/${toolId}/share`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ scope }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.success === false) {
    throw new Error((body as { message?: string }).message || `Failed to create share link (${res.status})`);
  }
  return { id: String(body.id), token: body.token, scope: body.scope };
}

export async function getShares(toolId: string): Promise<Share[]> {
  const res = await fetch(`${API_BASE}/api/MyTools/${toolId}/shares`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`Failed to load shares (${res.status})`);
  const json = await res.json();
  const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
  return list.map((s: any): Share => ({
    id: String(s.id),
    token: s.token,
    scope: s.scope,
    isActive: s.isActive,
    createdAt: s.createdAt,
    revokedAt: s.revokedAt ?? undefined,
  }));
}

export async function revokeShare(shareId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/MyTools/shares/${shareId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.success === false) {
    throw new Error((body as { message?: string }).message || `Failed to revoke share (${res.status})`);
  }
}

export async function regenerateShare(shareId: string): Promise<{ id: string; token: string }> {
  const res = await fetch(`${API_BASE}/api/MyTools/shares/${shareId}/regenerate`, {
    method: 'POST',
    headers: authHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.success === false) {
    throw new Error((body as { message?: string }).message || `Failed to regenerate share (${res.status})`);
  }
  return { id: String(body.id), token: body.token };
}

export async function refreshShare(shareId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/MyTools/shares/${shareId}/refresh`, {
    method: 'POST',
    headers: authHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.success === false) {
    throw new Error((body as { message?: string }).message || `Failed to refresh share (${res.status})`);
  }
}

// ── Widgets ──────────────────────────────────────────────────────────────────
// A Widget tool's code lives in a server-validated, flattened HTML document that
// the frontend renders in a sandboxed iframe (see features/widgets/WidgetFrame).

export type WidgetPermission =
  | 'forms' | 'popups' | 'modals'
  | 'open-links' | 'clipboard-write' | 'ui:navigate' | 'ui:notify' | 'chat:prefill' | 'realtime:kanban'
  | `api:${string}` | `network:${string}`;
export type WidgetSurface = 'page' | 'sidebar' | 'tile';

/** An authenticated third-party API this widget calls via Momentum.connect. The host resolves the
 *  destination (baseUrl) and the credential (installer-supplied, stored server-side) — the widget's
 *  own script only ever picks a relative path/method/query/body. See docs/widgets/README.md. */
export interface WidgetConnection {
  key: string;
  label: string;
  baseUrl: string;
  headerName: string;
  headerPrefix: string;
  hint?: string;
  scope: 'shared' | 'per_user';
  required: boolean;
}

export interface WidgetManifest {
  name: string;
  version: string;
  description?: string;
  entry: string;
  icon?: string;
  permissions?: WidgetPermission[];
  config?: UpsertMyToolConfigFieldSchema[];
  connections?: WidgetConnection[];
  surfaces?: WidgetSurface[];
  minHeight?: number;
  autoResize?: boolean;
  /** What changed in this version (≤ 2000 chars) — shown in Versions and to reviewers. */
  changelog?: string;
  /** Served asset path of the bundle's README (rewritten to "assets/readme.md" at upload). */
  readme?: string;
  /** Served asset paths of up to 4 screenshots (rewritten to "assets/shot-N.ext" at upload). */
  screenshots?: string[];
}

export interface WidgetBundleUploadResult {
  manifest: WidgetManifest;
  version: string;
  revision: number;
  sizeBytes: number;
  grantedSandbox: string[];
  warnings: string[];
  visibilityDemoted: boolean;
  /** A pending Global review request was withdrawn because the code changed; the owner must resubmit. */
  pendingWithdrawn: boolean;
}

export interface WidgetManifestInfo {
  toolId: string;
  name: string;
  icon: string;
  manifest: WidgetManifest;
  revision: number;
  version: string;
  grantedSandbox: string[];
  csp: string;
  configSchema: UpsertMyToolConfigFieldSchema[];
}

export interface InstalledWidget {
  id: string;
  installationId: string;
  name: string;
  icon: string;
  description: string;
  version: string;
  /** Publishing agency's name, resolved by the API; empty when an older API doesn't send it. */
  publisherName: string;
  /** Manifest as this install runs it: permissions trimmed and surfaces overridden by the installer's controls. */
  manifest: WidgetManifest | null;
  /** Manifest exactly as uploaded. */
  fullManifest: WidgetManifest | null;
  controls: WidgetInstallControls;
  paused: boolean;
  /** Permissions a newer upload asks for that this person has not yet approved (off until they do). */
  pendingPermissions: string[];
  grantedSandbox: string[];
  /** What the widget receives via momentum:init (reserved control keys removed). */
  configValues: Record<string, string>;
  /** Everything stored on the installation, including control keys — needed to write changes back. */
  rawConfigValues: Record<string, string>;
}

export function parseWidgetManifest(raw: unknown): WidgetManifest | null {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) as WidgetManifest; } catch { return null; }
  }
  return raw as WidgetManifest;
}

const SANDBOX_BY_PERMISSION: Record<string, string> = {
  forms: 'allow-forms',
  popups: 'allow-popups',
  modals: 'allow-modals',
};

/** Client-side mirror of the server's grantedSandbox — used only for the pre-save
 * local preview. Never emits allow-same-origin. */
export function sandboxFromManifest(manifest: WidgetManifest | null | undefined): string[] {
  const tokens = ['allow-scripts'];
  for (const p of manifest?.permissions ?? []) {
    const token = SANDBOX_BY_PERMISSION[p];
    if (token && !tokens.includes(token)) tokens.push(token);
  }
  return tokens;
}

export function widgetHasSurface(manifest: WidgetManifest | null | undefined, surface: WidgetSurface): boolean {
  const surfaces = manifest?.surfaces;
  if (!surfaces || surfaces.length === 0) return surface === 'page';
  return surfaces.includes(surface);
}

/** Everything manifest.json used to be hand-authored for, now collected as wizard input instead.
 *  Any field left undefined here leaves the corresponding manifest field alone (falls back to a
 *  bundle's own manifest.json, if it still has one, or the default). A field given here always
 *  wins, the same way scanned code always wins for permissions. */
export interface WidgetBundleOverrides {
  connections?: WidgetConnection[];
  surfaces?: WidgetSurface[];
  version?: string;
  minHeight?: number;
  autoResize?: boolean;
}

export async function uploadWidgetBundle(toolId: string, file: File, overrides?: WidgetBundleOverrides): Promise<WidgetBundleUploadResult> {
  if (isWidgetsMockEnabled()) {
    const bundle = await buildAndStoreMockBundle(toolId, file, file.name.replace(/\.[^.]+$/, ''));
    return {
      manifest: bundle.manifest,
      version: bundle.version,
      revision: bundle.revision,
      sizeBytes: file.size,
      grantedSandbox: sandboxFromManifest(bundle.manifest),
      warnings: bundle.warnings,
      visibilityDemoted: false,
      pendingWithdrawn: false,
    };
  }
  const formData = new FormData();
  formData.append('formFile', file);
  // From the wizard's own fields, not manifest.json — replaces whatever the bundle's own
  // manifest.json (if any) separately declared, the same way scanned permissions always win.
  if (overrides?.connections) formData.append('connectionsJson', JSON.stringify(overrides.connections));
  if (overrides?.surfaces) formData.append('surfacesJson', JSON.stringify(overrides.surfaces));
  if (overrides?.version !== undefined) formData.append('versionOverride', overrides.version);
  if (overrides?.minHeight !== undefined) formData.append('minHeight', String(overrides.minHeight));
  if (overrides?.autoResize !== undefined) formData.append('autoResize', String(overrides.autoResize));
  const res = await fetch(`${API_BASE}/api/MyTools/${toolId}/widget-bundle`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` },
    body: formData,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.success === false) {
    const errors: string[] = Array.isArray(body?.errors) ? body.errors : [];
    const detail = errors.length > 1 ? `\n• ${errors.join('\n• ')}` : '';
    throw new Error(((body as { message?: string }).message || `Failed to upload widget bundle (${res.status})`) + detail);
  }
  const d = body.data ?? {};
  return {
    manifest: d.manifest,
    version: d.version ?? d.manifest?.version ?? '1.0.0',
    revision: Number(d.revision ?? 0),
    sizeBytes: Number(d.sizeBytes ?? 0),
    grantedSandbox: Array.isArray(d.grantedSandbox) ? d.grantedSandbox : ['allow-scripts'],
    warnings: Array.isArray(d.warnings) ? d.warnings : [],
    visibilityDemoted: !!d.visibilityDemoted,
    pendingWithdrawn: !!d.pendingWithdrawn,
  };
}

export interface WidgetBundlePreview {
  permissions: WidgetPermission[];
  warnings: string[];
  /** The document a real upload would store (CSP + SDK prelude + flattened code); null from an
   *  API that predates it. Preview this, never the raw file — raw HTML has no window.Momentum. */
  html: string | null;
}

/** Dry run of the same scanner a real upload runs — nothing is saved. Lets the wizard show
 *  "this will request…" the moment a file is chosen, before a real upload+review cycle. */
export async function previewWidgetBundle(file: File, signal?: AbortSignal): Promise<WidgetBundlePreview> {
  const formData = new FormData();
  formData.append('formFile', file);
  const res = await fetch(`${API_BASE}/api/MyTools/widget-bundle/preview`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` },
    body: formData,
    signal,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.success === false) {
    // Same shape as a real upload's rejection: every problem, not just the headline.
    const errors: string[] = Array.isArray(body?.errors) ? body.errors : [];
    const detail = errors.length > 1 ? `\n• ${errors.join('\n• ')}` : '';
    throw new Error(((body as { message?: string }).message || `Couldn't scan this file (${res.status})`) + detail);
  }
  return {
    permissions: Array.isArray(body.permissions) ? body.permissions : [],
    warnings: Array.isArray(body.warnings) ? body.warnings : [],
    html: typeof body.html === 'string' ? body.html : null,
  };
}

export async function fetchWidgetContent(toolId: string, signal?: AbortSignal): Promise<string> {
  if (isWidgetsMockEnabled()) {
    const bundle = getMockWidgetBundle(toolId);
    if (!bundle) throw new Error('No bundle in the dev mock store for this widget — upload one from the wizard (Edit → Widget bundle).');
    return bundle.html;
  }
  const res = await fetch(`${API_BASE}/api/MyTools/${toolId}/widget/content`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` },
    signal,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { message?: string }).message || `Failed to load widget (${res.status})`);
  }
  return res.text();
}

export async function fetchWidgetManifest(toolId: string, signal?: AbortSignal): Promise<WidgetManifestInfo> {
  if (isWidgetsMockEnabled()) {
    const bundle = getMockWidgetBundle(toolId);
    if (!bundle) throw new Error('No bundle in the dev mock store for this widget.');
    return {
      toolId: String(toolId),
      name: bundle.manifest.name,
      icon: bundle.manifest.icon ?? '',
      manifest: bundle.manifest,
      revision: bundle.revision,
      version: bundle.version,
      grantedSandbox: sandboxFromManifest(bundle.manifest),
      csp: '',
      configSchema: bundle.manifest.config ?? [],
    };
  }
  const res = await fetch(`${API_BASE}/api/MyTools/${toolId}/widget/manifest`, { headers: authHeaders(), signal });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.success === false) {
    throw new Error((body as { message?: string }).message || `Failed to load widget manifest (${res.status})`);
  }
  const d = body.data ?? {};
  return {
    toolId: String(d.toolId),
    name: d.name ?? '',
    icon: d.icon ?? '',
    manifest: d.manifest,
    revision: Number(d.revision ?? 0),
    version: d.version ?? d.manifest?.version ?? '1.0.0',
    grantedSandbox: Array.isArray(d.grantedSandbox) ? d.grantedSandbox : ['allow-scripts'],
    csp: d.csp ?? '',
    configSchema: Array.isArray(d.configSchema) ? d.configSchema : [],
  };
}

export function parseConfigValues(raw: unknown): Record<string, string> {
  if (!raw) return {};
  let obj: unknown = raw;
  if (typeof raw === 'string') {
    try { obj = JSON.parse(raw); } catch { return {}; }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return {};
  return Object.fromEntries(Object.entries(obj as Record<string, unknown>).map(([k, v]) => [k, v == null ? '' : String(v)]));
}

/** Installed Widget tools only (type 3), with the install-time config values the
 * widget receives via momentum:init. */
export async function listInstalledWidgets(signal?: AbortSignal): Promise<InstalledWidget[]> {
  const res = await fetch(`${API_BASE}/api/MyTools/installed`, { headers: authHeaders(), signal });
  if (!res.ok) throw new Error(`Failed to load installed widgets (${res.status})`);
  const json = await res.json();
  const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
  return list
    .filter(t => Number(t.type) === 3)
    .map((t): InstalledWidget => {
      // Until the migration ships, the API has no widget columns — fall back to the dev mock store.
      const mock = getMockWidgetBundle(String(t.id));
      const fullManifest = parseWidgetManifest(t.widgetManifest) ?? mock?.manifest ?? null;
      const rawConfigValues = { ...(getMockInstallConfig(String(t.id)) ?? {}), ...parseConfigValues(t.installationConfig) };
      const controls = readInstallControls(rawConfigValues);
      const manifest = applyInstallControls(fullManifest, controls);
      return {
        id: String(t.id),
        installationId: String(t.installationId ?? t.id),
        name: t.name ?? fullManifest?.name ?? '',
        icon: t.icon || fullManifest?.icon || '📊',
        description: t.description ?? fullManifest?.description ?? '',
        version: t.widgetVersion ?? mock?.version ?? fullManifest?.version ?? '1.0.0',
        publisherName: t.publisherName ?? '',
        manifest,
        fullManifest,
        controls,
        paused: controls.paused,
        pendingPermissions: pendingPermissions(fullManifest, controls),
        grantedSandbox: sandboxFromManifest(manifest),
        configValues: stripControlKeys(rawConfigValues),
        rawConfigValues,
      };
    });
}

/** Replaces an installation's saved config (widget settings + per-install controls).
 *  connectionValues, when given, sets (non-empty) or clears (empty string) manifest.connections
 *  secrets — those never travel through `values`/momentum:init, only through this dedicated field. */
export async function updateInstallationConfig(toolId: string, installationId: string, values: Record<string, string>, connectionValues?: Record<string, string>): Promise<void> {
  const res = await fetch(`${API_BASE}/api/MyTools/installation/${installationId}/config`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ mcpFieldValues: values, connectionValues }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.success === false) {
    throw new Error((body as { message?: string }).message || `Failed to update configuration (${res.status})`);
  }
  if (getMockWidgetBundle(toolId)) replaceMockInstallConfig(toolId, values);
  notifyWidgetInstallsChanged();
}

/** Which of an installation's manifest.connections have a secret configured — never the values. */
export async function getWidgetConnectionKeys(installationId: string, signal?: AbortSignal): Promise<string[]> {
  const res = await fetch(`${API_BASE}/api/MyTools/installation/${installationId}/connections`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` },
    signal,
  });
  if (!res.ok) return [];
  const body = await res.json().catch(() => ({}));
  return Array.isArray(body?.data) ? body.data : [];
}

export interface WidgetAsset {
  /** Displayable URL (object URL or data: URI). Revoke object URLs when done. */
  url: string;
  /** Decoded text for text assets (README); null for images. */
  text: string | null;
  contentType: string;
}

/** A listing asset the manifest declares (README, screenshot) — GET /api/MyTools/{id}/widget/asset?path=… */
export async function fetchWidgetAsset(toolId: string, path: string, signal?: AbortSignal): Promise<WidgetAsset> {
  const mock = getMockWidgetAsset(toolId, path);
  if (mock !== null) {
    if (mock.startsWith('data:')) return { url: mock, text: null, contentType: mock.slice(5, mock.indexOf(';')) };
    return { url: `data:text/markdown;charset=utf-8,${encodeURIComponent(mock)}`, text: mock, contentType: 'text/markdown' };
  }
  const res = await fetch(`${API_BASE}/api/MyTools/${toolId}/widget/asset?path=${encodeURIComponent(path)}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` },
    signal,
  });
  if (!res.ok) throw new Error(`Failed to load widget asset (${res.status})`);
  const contentType = res.headers.get('content-type') ?? 'application/octet-stream';
  const blob = await res.blob();
  const text = /^text\//i.test(contentType) ? await blob.text() : null;
  return { url: URL.createObjectURL(blob), text, contentType };
}

/** One uploaded revision of a widget (row of my_tool_widget_bundles). */
export interface WidgetBundleInfo {
  id: number;
  toolId: string;
  revision: number;
  version: string;
  manifest: string | null;
  entryFile: string | null;
  fileCount: number | null;
  sizeBytes: number | null;
  flattenedSizeBytes: number | null;
  createdAt: string | null;
}

/** Revision history of a widget the caller owns — GET /api/MyTools/{id}/widget/bundles. */
export async function fetchWidgetBundles(toolId: string, signal?: AbortSignal): Promise<WidgetBundleInfo[]> {
  const mock = getMockWidgetBundleHistory(toolId);
  if (mock) {
    return mock.map(r => ({
      id: r.id, toolId: r.toolId, revision: r.revision, version: r.version, manifest: r.manifest, entryFile: r.entryFile,
      fileCount: r.fileCount, sizeBytes: r.sizeBytes, flattenedSizeBytes: r.flattenedSizeBytes, createdAt: r.createdAt,
    }));
  }
  const res = await fetch(`${API_BASE}/api/MyTools/${toolId}/widget/bundles`, { headers: authHeaders(), signal });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.success === false) {
    throw new Error((body as { message?: string }).message || `Failed to load version history (${res.status})`);
  }
  const rows: Array<Record<string, unknown>> = Array.isArray(body.data) ? body.data : [];
  return rows.map(r => ({
    id: Number(r.id ?? 0),
    toolId: String(r.toolId ?? toolId),
    revision: Number(r.revision ?? 0),
    version: String(r.version ?? ''),
    manifest: typeof r.manifest === 'string' ? r.manifest : null,
    entryFile: typeof r.entryFile === 'string' ? r.entryFile : null,
    fileCount: typeof r.fileCount === 'number' ? r.fileCount : null,
    sizeBytes: typeof r.sizeBytes === 'number' ? r.sizeBytes : null,
    flattenedSizeBytes: typeof r.flattenedSizeBytes === 'number' ? r.flattenedSizeBytes : null,
    createdAt: typeof r.createdAt === 'string' ? r.createdAt : null,
  }));
}

export async function resolveShareToken(token: string): Promise<ResolvedShare> {
  const res = await fetch(`${API_BASE}/api/MyTools/shares/resolve/${token}`, { headers: authHeaders() });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.success === false) {
    throw new Error((body as { message?: string }).message || `Failed to resolve share link (${res.status})`);
  }
  const data = body.data ?? {};
  return {
    copiedToolId: String(data.copiedToolId),
    toolType: RESOLVE_INT_TO_TOOL_TYPE[data.toolType] ?? 'skill',
    toolName: data.toolName ?? '',
    toolDescription: data.toolDescription ?? '',
    toolIcon: data.toolIcon ?? '',
    toolCategory: data.toolCategory ?? '',
    mcpUrl: data.mcpUrl ?? undefined,
    mcpHeaderKeys: Array.isArray(data.mcpHeaderKeys) ? data.mcpHeaderKeys : [],
    mcpInstallerFields: Array.isArray(data.mcpInstallerFields)
      ? data.mcpInstallerFields.map((f: any): ShareInstallerField => ({
          key: f.key, label: f.label || f.key, hint: f.hint || undefined, scope: f.scope === 'per_user' ? 'per_user' : 'shared',
        }))
      : [],
    apiIntegrationId: data.apiIntegrationId ?? undefined,
    isAgencyOwner: !!data.isAgencyOwner,
  };
}
